<template>
  <v-card>
    <v-card-title class="pb-1">
      <span class="text-truncate">{{ ip }}</span>
      <v-spacer></v-spacer>
      <v-btn icon :loading="loading" @click="load()">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-alert v-if="error" type="warning" dense text class="mx-4">
      {{ error }}
    </v-alert>

    <template v-if="detail">
      <v-card-subtitle class="pt-0">
        {{ place }}
        <span v-if="detail.geo && detail.geo.isp"><br>{{ detail.geo.isp }}</span>
        <span v-if="detail.geo && detail.geo.asn"><br>{{ detail.geo.asn }}</span>
      </v-card-subtitle>

      <v-card-text class="pt-0">
        <div class="mb-2">
          <strong>{{ formatNumber(detail.requests_total) }}</strong> Requests
          in {{ windowLabel }}
        </div>
        <div class="text--secondary mb-3">
          {{ formatTs(detail.first_seen) }} bis {{ formatTs(detail.last_seen) }}
        </div>

        <!-- Die Chips sind der Filter für die Requestliste unten: ein Klick zeigt
             nur Zeilen mit diesem Status, ein zweiter hebt ihn auf. Gefiltert
             wird im Backend — geladen sind nur 50 Zeilen, ein Filter im Browser
             hätte aus "3 Treffer" beliebig weniger gemacht. -->
        <div class="mb-1">
          <v-chip
              v-for="entry in statusEntries"
              :key="entry.status"
              small
              class="mr-1 mb-1 chip-filter"
              :color="statusColor(entry.status)"
              :outlined="statusFilter !== null && statusFilter !== entry.status"
              @click="toggleStatus(entry.status)"
          >
            {{ entry.status }}: {{ formatNumber(entry.count) }}
          </v-chip>
        </div>
        <div class="caption text--secondary mb-4">
          <template v-if="statusFilter">
            Liste gefiltert auf Status {{ statusFilter }}
            ({{ formatNumber(filteredTotal) }})
            <v-btn x-small text class="ml-1" @click="toggleStatus(statusFilter)">
              Filter aufheben
            </v-btn>
          </template>
          <template v-else>Status anklicken, um die Liste unten zu filtern</template>
        </div>

        <div class="subtitle-2 mb-1">Häufigste Pfade</div>
        <v-simple-table dense class="mb-4">
          <template v-slot:default>
            <tbody>
              <tr v-for="entry in detail.top_uris" :key="entry.uri">
                <td class="text-truncate" style="max-width: 190px">{{ entry.uri }}</td>
                <td class="text-right" style="white-space: nowrap">{{ formatNumber(entry.count) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div class="subtitle-2 mb-1">User-Agents</div>
        <div
            v-for="entry in detail.user_agents"
            :key="entry.user_agent || 'unbekannt'"
            class="caption text--secondary mb-1"
        >
          {{ entry.user_agent || 'unbekannt' }} ({{ formatNumber(entry.count) }})
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text>
        <div class="subtitle-2 mb-1">
          Letzte Requests<span v-if="statusFilter"> · Status {{ statusFilter }}</span>
        </div>
        <div v-if="!requests.length" class="caption text--secondary">
          <template v-if="statusFilter">
            Keine Requests mit Status {{ statusFilter }} in {{ windowLabel }}.
          </template>
          <template v-else>Keine Requests in {{ windowLabel }}.</template>
        </div>
        <v-simple-table v-else dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Zeit</th>
                <th class="text-left">Dienst</th>
                <th class="text-left">Pfad</th>
                <th class="text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in requests" :key="index">
                <td class="text-left caption">{{ formatTime(entry.ts) }}</td>
                <!-- Das Panel ist nur ein Drittel breit, Dienst und Pfad müssen
                     sich die Zeile teilen — beide gekürzt, der ganze Wert steht
                     im title. -->
                <td class="text-left caption text-truncate" style="max-width: 90px"
                    :title="entry.host || 'Dienst nicht mitgeschrieben'">
                  {{ entry.host || '–' }}
                </td>
                <td class="text-left caption text-truncate" style="max-width: 110px"
                    :title="`${entry.method || ''} ${entry.uri || ''}`.trim()">
                  {{ entry.method }} {{ entry.uri }}
                </td>
                <td class="text-right caption">{{ entry.status }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <!-- Ohne diesen Satz liest sich ein "–" wie "Dienst unbekannt/kaputt".
             `host` wird erst seit dem 20.08.2026 mitgeschrieben, ältere Zeilen
             haben schlicht keinen — gleiche Erklärung wie in UsageStats.vue. -->
        <div v-if="hasUnknownHost" class="caption text--secondary mt-2">
          Zeilen mit „–“ stammen aus der Zeit, bevor nginx den Dienst
          mitgeschrieben hat.
        </div>
        <v-btn
            v-if="hasMore"
            small
            text
            block
            class="mt-2"
            :loading="loadingMore"
            :disabled="loadingMore"
            @click="loadMore"
        >
          mehr laden
        </v-btn>
      </v-card-text>
    </template>

    <v-card-text v-else-if="!loading && !error" class="text--secondary">
      Keine Requests dieser IP im Zeitraum.
    </v-card-text>
  </v-card>
</template>

<script>
import { weblogApi } from '../../services/weblog'

const PAGE_SIZE = 50

const WINDOW_LABELS = {
  1: 'der letzten Stunde',
  24: 'den letzten 24 Stunden',
  168: 'den letzten 7 Tagen',
  720: 'den letzten 30 Tagen',
}

export default {
  name: 'IpDetailPanel',
  props: {
    ip: { type: String, required: true },
    hours: { type: Number, default: 24 },
  },
  data: () => ({
    detail: null,
    requests: [],
    hasMore: false,
    loading: false,
    loadingMore: false,
    error: null,
    requestSeq: 0,
    // Aktiver Status-Chip; null = kein Filter.
    statusFilter: null,
  }),
  computed: {
    place() {
      const geo = this.detail && this.detail.geo
      if (!geo) return 'Ort unbekannt'
      const parts = [geo.city, geo.region, geo.country].filter(Boolean)
      return parts.length ? parts.join(', ') : 'Ort unbekannt'
    },
    windowLabel() {
      return WINDOW_LABELS[this.hours] || `${this.hours} h`
    },
    statusEntries() {
      const counts = (this.detail && this.detail.status_counts) || {}
      return Object.keys(counts)
          .sort()
          .map((status) => ({ status, count: counts[status] }))
    },
    filteredTotal() {
      return (this.detail && this.detail.filtered_total) || 0
    },
    hasUnknownHost() {
      return this.requests.some((entry) => !entry.host)
    },
  },
  watch: {
    // Filter zurücksetzen: ein Status, den die neue Adresse oder das neue
    // Fenster nicht hat, ließe die Liste ohne sichtbaren Grund leer.
    ip() {
      this.statusFilter = null
      this.load()
    },
    hours() {
      this.statusFilter = null
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    formatNumber(value) {
      return (value || 0).toLocaleString('de-DE')
    },
    formatTs(value) {
      if (!value) return '–'
      return new Date(value).toLocaleString('de-DE')
    },
    formatTime(value) {
      if (!value) return '–'
      // Bei 7 Tagen Fenster liegen die neuesten 50 Requests einer ruhigen
      // Adresse oft Tage auseinander — zwei Zeilen mit derselben Uhrzeit
      // wären dann ohne Datum nicht unterscheidbar. Bei 1 h/24 h passt das
      // ganze Fenster in einen Tag, da reicht die kompakte Uhrzeit.
      if (this.hours > 24) return this.formatTs(value)
      return new Date(value).toLocaleTimeString('de-DE')
    },
    // status_counts hat String-Keys, weil JSON keine Integer-Keys kennt, und
    // enthält zusätzlich den Eimer "unknown" für Requests, deren Status NULL war.
    // Ein String-Vergleich `status >= '400'` würde "unknown" als Fehler einfärben,
    // weil 'u' hinter '4' sortiert — deshalb wird numerisch verglichen und alles
    // Nicht-Numerische neutral dargestellt.
    statusColor(status) {
      const code = Number(status)
      if (!Number.isFinite(code)) return 'grey lighten-1'
      return code >= 400 ? 'red lighten-4' : 'green lighten-4'
    },
    toggleStatus(status) {
      // Zweiter Klick auf denselben Chip hebt den Filter auf.
      this.statusFilter = this.statusFilter === status ? null : status
      // keepDetail: beim Filtern bleibt die Adresse dieselbe, also dürfen Kopf
      // und Chips stehen bleiben. Würden sie wie beim IP-Wechsel geleert,
      // verschwänden genau die Chips, auf die man gerade geklickt hat.
      this.load({ keepDetail: true })
    },
    async load({ keepDetail = false } = {}) {
      // Jede Anfrage bekommt eine Nummer. Trifft eine Antwort ein, deren Nummer
      // nicht mehr die aktuelle ist, wurde sie überholt und wird verworfen —
      // sonst schreibt eine langsame Antwort ihre Daten über eine neuere.
      const seq = ++this.requestSeq
      // Vor dem Warten leeren: die Kopfzeile zeigt `ip` sofort aus dem Prop, und
      // ohne das Leeren stünden darunter noch Ort, ISP und Tabellen der zuvor
      // gewählten Adresse — das Panel würde für einen Moment Unsinn behaupten.
      if (!keepDetail) this.detail = null
      this.requests = []
      this.hasMore = false
      this.loading = true
      this.error = null
      try {
        const data = await weblogApi.ip(this.ip, this.hours, PAGE_SIZE, 0, this.statusFilter)
        if (seq !== this.requestSeq) return
        this.detail = data
        this.requests = data.requests
        this.hasMore = data.has_more
      } catch (e) {
        if (seq !== this.requestSeq) return
        console.error('[IpDetailPanel]', e)
        // 404 heißt hier nicht "Fehler", sondern "im Fenster nichts von der IP" —
        // etwa wenn man von 7 Tagen auf 1 Stunde umschaltet.
        if (`${e.message}`.indexOf('HTTP 404') !== -1) {
          this.detail = null
          this.requests = []
          this.hasMore = false
        } else {
          this.error = 'Details konnten nicht geladen werden'
        }
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    async loadMore() {
      // Vuetifys `loading` am Button zeigt nur einen Spinner, es sperrt ihn nicht.
      // Ohne diese Wache holen zwei schnelle Klicks denselben Offset und hängen
      // dieselben 50 Zeilen zweimal an.
      if (this.loadingMore) return
      const seq = this.requestSeq
      this.loadingMore = true
      try {
        const data = await weblogApi.ip(
            this.ip, this.hours, PAGE_SIZE, this.requests.length, this.statusFilter)
        // Hat inzwischen ein `load()` die Liste zurückgesetzt, gehören diese
        // Zeilen zu einem anderen Zustand und werden verworfen.
        if (seq !== this.requestSeq) return
        this.requests = this.requests.concat(data.requests)
        this.hasMore = data.has_more
        this.error = null
      } catch (e) {
        if (seq !== this.requestSeq) return
        console.error('[IpDetailPanel] loadMore', e)
        this.error = 'Weitere Requests konnten nicht geladen werden'
      } finally {
        this.loadingMore = false
      }
    },
  },
}
</script>

<style scoped>
/* #app setzt global text-align: center — für ein Datenpanel mit Fließtext,
   Kennzahlen und Tabellen wirkt das wie ein Plakat statt wie ein Datenblatt.
   Analog zu EndpointCard.vue:88, KvTree.vue:64 und RobotStatusDialog.vue:110
   lokal auf linksbündig umstellen; die Zahlenspalten bleiben über die
   text-right-Utility rechtsbündig, da diese mit !important arbeitet und
   damit Vorrang vor der geerbten Ausrichtung hat. */
.v-card {
  text-align: left;
}

/* Die Status-Chips sind Schaltflächen für den Listenfilter, nicht nur Anzeige. */
.chip-filter {
  cursor: pointer;
}
</style>
